import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { map as lodashMap } from 'lodash';

import { IManualAuditRuleReport } from '../../../../../../../../shared/interfaces/audit-reports/audit-report.interface';
import { $severity } from '../../../../../../../../shared/constants/accessibility';
import { ISuccessCriteria } from '../../../../../../../../shared/audits/definitions/success-criteria/success-criteria.interface';
import { AuditRuleService } from '../../../../../../services/audit-rule.service';
import { IAuditRuleServerItem, IAuditRuleServerResponse } from '../../../../../../../../shared/interfaces/audit-rule.interface';
import { $auditRule, $auditRuleSearch } from '../../../../../../../../shared/constants/audit-rule';
import { AuditToolNames } from '../../../../../../../../shared/constants/audit-tool';
import { SuccessCriteriaService } from '../../../../../../services/success-criteria.service';
import { $auditStandardSuccessCriteria } from '../../../../../../../../shared/constants/audit-standard-success-criteria';
import { ICustomSeveritiesMap } from '../../../../../../../../shared/interfaces/tenant.interface';
import { IPopulatedManualAuditIssueServerResponse } from '../../../../../../../../shared/interfaces/manual-audit.interface';
import {
  IPopulatedManualFindingClient,
  IPopulatedManualFindingSuccessCriteria,
} from '../../../../../../../../shared/interfaces/manual-audit-finding.interface';
import { $auditFinding } from '../../../../../../../../shared/constants/audit-finding';
import {
  EVALUATION_STANDARD_TO_AUDIT_STANDARD,
  EvaluationStandards,
} from '../../../../../../../../shared/constants/manual-evaluation';
import { SharedAuditsUtility } from '../../../../../../../../shared/utils/audits.utility';
import { AngularUtility } from '../../../../../../utility/angular.utility';
import { SharedCommonUtility } from '../../../../../../../../shared/utils/common.utility';
import { validationError } from '../../../../../../constants/form.constants';

@Component({
  selector: 'app-editable-manual-rule-details',
  templateUrl: './editable-manual-rule-details.component.html',
  styleUrls: ['./editable-manual-rule-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditableManualRuleDetailsComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private serverResponse: IPopulatedManualAuditIssueServerResponse;
  private itemSelected$: BehaviorSubject<string>;
  private offeredAuditRules$: Subject<IAuditRuleServerItem[]> = new Subject<IAuditRuleServerItem[]>();
  private blur$: Subject<void> = new Subject<void>();

  private static RULE_COUNT_SEARCH_LIMIT: number = 20;

  @Input()
  public form: UntypedFormGroup;
  @Input()
  public field: string;
  @Input()
  public customSeveritiesData: ICustomSeveritiesMap;
  @Input()
  public customSeverityKeysSortedByRank: $severity[];
  @Input()
  public formValidationRequest$: Observable<void>;
  @Input()
  public testGuideId: string;
  @Output()
  public selectedRuleChange: EventEmitter<IAuditRuleServerItem> = new EventEmitter<IAuditRuleServerItem>();

  @Input()
  public set issueResponse(response: IPopulatedManualAuditIssueServerResponse) {
    this.serverResponse = response;
    this.buildRuleReport(response.issue);
  }

  public ruleReport: IManualAuditRuleReport;
  public searchFunction: (text$: Observable<string>) => Observable<string[]> = this.search.bind(this);

  constructor(
    private auditRuleService: AuditRuleService,
    private successCriteriaService: SuccessCriteriaService,
  ) {}

  public get successCriterias(): Set<string> {
    return new Set(
      this.ruleReport.successCriterias.map((criteria: ISuccessCriteria): string => `${criteria.num} - ${criteria.handle}`),
    );
  }

  private buildRuleReport(issue: IPopulatedManualFindingClient): void {
    const successCriterias: IPopulatedManualFindingSuccessCriteria[] = SharedAuditsUtility.getScopedSuccessCriteria(
      issue[$auditFinding.ruleId][$auditRule.successCriterias],
      this.serverResponse.manualAuditStandards,
    );

    const findingStandards: EvaluationStandards[] = SharedAuditsUtility.getEvaluationStandardsOfSuccessCriteria(
      this.serverResponse.manualAuditStandards,
      successCriterias,
    );

    this.ruleReport = {
      ruleDescription: issue[$auditFinding.ruleId][$auditRule.description],
      ruleId: issue[$auditFinding.ruleId][$auditRule.ruleId],
      severity: issue[$auditFinding.ruleId][$auditRule.severity] as $severity,
      successCriterias: this.successCriteriaService.getSuccessCriteriaListFromStandards(
        lodashMap(successCriterias, $auditStandardSuccessCriteria.identifier),
        findingStandards.map((standard: EvaluationStandards) => EVALUATION_STANDARD_TO_AUDIT_STANDARD[standard]),
      ),
      standards: findingStandards,
      disabilitiesAffected: issue[$auditFinding.disabilityAffected],
    };
  }

  private search(text$: Observable<string>): Observable<string[]> {
    return text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      filter((term: string) => term.length > 2),
      switchMap((term: string) =>
        this.auditRuleService.findCurrentDigitalPropertyAuditRules(
          EditableManualRuleDetailsComponent.RULE_COUNT_SEARCH_LIMIT,
          0,
          {
            [$auditRuleSearch.ruleIdSubstring]: [term],
            [$auditRuleSearch.tools]: [AuditToolNames.manual],
            ...(SharedCommonUtility.notNullish(this.testGuideId) ? { [$auditRuleSearch.testGuideId]: [this.testGuideId] } : {}),
          },
        ),
      ),
      map((response: IAuditRuleServerResponse) => response.auditRules as IAuditRuleServerItem[]),
      tap((auditRules: IAuditRuleServerItem[]) => this.offeredAuditRules$.next(auditRules)),
      map((auditRules: IAuditRuleServerItem[]) => {
        return auditRules.map((rule: IAuditRuleServerItem) => rule.ruleId);
      }),
    );
  }

  public format(rule: IAuditRuleServerItem): string {
    return rule.ruleId;
  }

  public onEnter(event: any): void {
    event.preventDefault();
  }

  public onSelectItem(event: NgbTypeaheadSelectItemEvent<string>): void {
    this.itemSelected$.next(event.item);
  }

  public getCustomSeverityText(severity: $severity): string {
    return this.customSeverityKeysSortedByRank?.includes(severity) ? this.customSeveritiesData.get(severity).label : null;
  }

  public ngOnInit(): void {
    this.itemSelected$ = new BehaviorSubject<string>(this.ruleReport.ruleId);

    const selectedRule$: Observable<IAuditRuleServerItem> = this.itemSelected$.pipe(
      withLatestFrom(this.offeredAuditRules$),
      mergeMap(([selectedRuleId, offeredItems]: [string, IAuditRuleServerItem[]]) =>
        offeredItems.map((auditRule: IAuditRuleServerItem) => [selectedRuleId, auditRule]),
      ),
      filter(([selectedRuleId, offeredItem]: [string, IAuditRuleServerItem]) => selectedRuleId === offeredItem.ruleId),
      map(([, auditRule]: [string, IAuditRuleServerItem]) => auditRule),
      AngularUtility.shareRef(),
    );

    this.subscriptions.add(
      selectedRule$.subscribe((auditRule: IAuditRuleServerItem) => {
        this.buildRuleReport({
          ...this.serverResponse.issue,
          [$auditFinding.ruleId]: {
            ...this.serverResponse.issue[$auditFinding.ruleId],
            ...auditRule,
          },
        });
      }),
    );

    this.subscriptions.add(
      selectedRule$.subscribe((auditRule: IAuditRuleServerItem) => {
        this.selectedRuleChange.emit(auditRule);
      }),
    );

    this.subscriptions.add(
      this.blur$.pipe(withLatestFrom(this.itemSelected$)).subscribe(([, selectedItem]: [void, string]) => {
        this.form.controls[this.field].setValue(selectedItem || this.ruleReport.ruleId);
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onBlur(): void {
    this.blur$.next();
  }

  protected get ruleErrorMessage(): string | undefined {
    if (this.form.controls[this.field].errors?.[validationError.ruleDisabled]) {
      return 'form_validation_rule_disabled';
    }

    return undefined;
  }
}
